import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowRight, Lock, Unlock, Infinity } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * QuotaDisplay - Shows user's current quota usage for their plan
 * Supports compact (inline) and full (card) display modes
 */
export default function QuotaDisplay({ mode = 'full', showUpgrade = true }) {
  const [quotaData, setQuotaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuotaUsage();
  }, []);

  const fetchQuotaUsage = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/quota/my-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotaData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching quota:', err);
      setError('Unable to load quota information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !quotaData) {
    return null; // Silent fail for quota display
  }

  const { plan_name, role, usage } = quotaData;
  const isFreePlan = plan_name.toLowerCase().includes('free');

  // Map quota types to user-friendly labels
  const quotaLabels = {
    investor_previews: { label: 'Investor Previews', icon: '👀' },
    match_suggestions: { label: 'Match Suggestions', icon: '✨' },
    browsing: { label: 'Profile Browsing', icon: '🔍' },
    pitch_deck_previews: { label: 'Pitch Deck Views', icon: '📄' },
    messaging_initiations: { label: 'Message Initiations', icon: '💬' }
  };

  // Filter relevant quotas based on role
  const getRelevantQuotas = () => {
    if (role === 'founder') {
      return ['investor_previews', 'match_suggestions'];
    } else if (role === 'investor') {
      return ['browsing', 'pitch_deck_previews', 'messaging_initiations'];
    } else if (role === 'cofounder') {
      return ['match_suggestions'];
    }
    return [];
  };

  const relevantQuotas = getRelevantQuotas();

  // Compact mode - just a summary badge
  if (mode === 'compact') {
    const hasLimitedQuota = relevantQuotas.some(q => {
      const u = usage[q];
      return u.limit > 0 && u.remaining <= 2;
    });

    return (
      <div className="flex items-center gap-2">
        <Badge 
          variant={hasLimitedQuota ? "destructive" : "secondary"}
          className="text-xs"
        >
          {isFreePlan ? 'Free Plan' : plan_name}
        </Badge>
        {hasLimitedQuota && showUpgrade && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-6 px-2"
            onClick={() => navigate('/pricing')}
          >
            Upgrade <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        )}
      </div>
    );
  }

  // Full mode - detailed card view
  return (
    <Card className="p-4 bg-slate-50 border-slate-200" data-testid="quota-display">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-slate-800">Your Usage</h3>
          <p className="text-sm text-slate-500">
            {plan_name} • Resets monthly
          </p>
        </div>
        {isFreePlan && showUpgrade && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/pricing')}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
            data-testid="upgrade-button"
          >
            Upgrade <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {relevantQuotas.map(quotaType => {
          const quotaInfo = usage[quotaType];
          const { label, icon } = quotaLabels[quotaType] || { label: quotaType, icon: '📊' };
          const isUnlimited = quotaInfo.limit === -1 || quotaInfo.remaining === -1;
          const percentage = isUnlimited ? 100 : (quotaInfo.used / quotaInfo.limit) * 100;
          const isLow = !isUnlimited && quotaInfo.remaining <= 1;
          const isExhausted = !isUnlimited && quotaInfo.remaining === 0;

          return (
            <div key={quotaType} className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-700">
                  <span>{icon}</span>
                  {label}
                </span>
                <span className={`font-medium ${
                  isExhausted ? 'text-red-600' : 
                  isLow ? 'text-amber-600' : 
                  'text-slate-600'
                }`}>
                  {isUnlimited ? (
                    <span className="flex items-center gap-1">
                      <Infinity className="w-4 h-4" />
                      Unlimited
                    </span>
                  ) : (
                    `${quotaInfo.used} / ${quotaInfo.limit}`
                  )}
                </span>
              </div>
              
              {!isUnlimited && (
                <div className="relative">
                  <Progress 
                    value={percentage} 
                    className={`h-2 ${
                      isExhausted ? 'bg-red-100' : 
                      isLow ? 'bg-amber-100' : 
                      'bg-slate-200'
                    }`}
                  />
                  {isExhausted && (
                    <div className="absolute inset-0 flex items-center justify-end pr-1">
                      <Lock className="w-3 h-3 text-red-500" />
                    </div>
                  )}
                </div>
              )}
              
              {isExhausted && (
                <p className="text-xs text-red-600">
                  Limit reached. Upgrade to continue.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/**
 * QuotaGate - Wrapper component that checks quota before allowing action
 */
export function QuotaGate({ 
  quotaType, 
  children, 
  fallback = null,
  onQuotaExceeded 
}) {
  const [quotaStatus, setQuotaStatus] = useState({ loading: true, allowed: true });

  useEffect(() => {
    checkQuota();
  }, [quotaType]);

  const checkQuota = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/quota/check/${quotaType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotaStatus({
        loading: false,
        allowed: response.data.has_remaining,
        remaining: response.data.remaining,
        limit: response.data.limit
      });
    } catch (err) {
      // Fail open - allow access if quota check fails
      setQuotaStatus({ loading: false, allowed: true });
    }
  };

  if (quotaStatus.loading) {
    return <Loader2 className="w-4 h-4 animate-spin" />;
  }

  if (!quotaStatus.allowed) {
    if (onQuotaExceeded) {
      onQuotaExceeded(quotaStatus);
    }
    return fallback;
  }

  return children;
}

/**
 * useQuota - Hook for programmatic quota checking
 */
export function useQuota() {
  const [loading, setLoading] = useState(false);

  const checkQuota = async (quotaType) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/quota/check/${quotaType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return {
        allowed: response.data.has_remaining,
        remaining: response.data.remaining,
        limit: response.data.limit,
        used: response.data.used,
        planName: response.data.plan_name
      };
    } catch (err) {
      return { allowed: true }; // Fail open
    } finally {
      setLoading(false);
    }
  };

  const useQuotaAndCheck = async (quotaType) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/quota/check-and-use/${quotaType}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { allowed: true, ...response.data };
    } catch (err) {
      if (err.response?.status === 403) {
        return { 
          allowed: false, 
          ...err.response.data.detail 
        };
      }
      return { allowed: true }; // Fail open
    } finally {
      setLoading(false);
    }
  };

  return { checkQuota, useQuotaAndCheck, loading };
}

// Re-export useQuotaGate from the dedicated hooks file for backward compatibility
// This maintains existing imports while allowing new code to import from the proper location
export { useQuotaGate } from '../hooks/useQuotaGate';
